import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchSeoDashboard, fetchTierManifests, formatSeoApiError } from '../../../utils/adminSeo';

const LAUNCH_STEPS = [
  { label: 'Import & Sync → Bootstrap + Apply tier manifests', to: '/admin/seo/import' },
  { label: 'Warm T1 caches (fares, airlines, baggage, visa)', to: '/admin/seo/import' },
  { label: 'Generate T1/T2 draft pages', to: '/admin/seo/import' },
  { label: 'Content Audit + Page Health fixes', to: '/admin/seo/audit' },
  { label: 'Bulk approve passing drafts', to: '/admin/seo/import' },
  { label: 'Verify sitemaps (indexable only)', to: '/admin/seo/sitemaps' },
  { label: 'Cron Runs / API Failures (24h)', to: '/admin/seo/cron-runs' },
];

export default function SeoDashboard() {
  const [data, setData] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    Promise.all([fetchSeoDashboard(), fetchTierManifests().catch(() => null)])
      .then(([dash, man]) => {
        setData(dash);
        setManifest(man);
      })
      .catch((e) => setErr(formatSeoApiError(e)));
  }, []);

  if (err) return <p className="text-red-600">{err}</p>;
  if (!data) return <p>Loading dashboard…</p>;

  const cards = [
    ['Total pages', data.total_pages],
    ['Published', data.published_pages],
    ['Draft', data.draft_pages],
    ['Noindex (pub.)', data.noindex_pages],
    ['Index-ready', data.index_ready_pages],
    ['Low health (<60)', data.low_health_pages],
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold text-[#1B375B]">Dashboard</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {cards.map(([label, val]) => (
          <div key={label} className="bg-white border rounded-lg p-4 shadow-sm">
            <div className="text-xs text-gray-500">{label}</div>
            <div className="text-2xl font-semibold">{val}</div>
          </div>
        ))}
      </div>

      {manifest?.manifest_keys && (
        <div className="bg-white border rounded-lg p-4 text-sm">
          <h3 className="font-medium mb-2">Tier manifest keys</h3>
          <div className="flex flex-wrap gap-3 text-xs">
            {Object.entries(manifest.manifest_keys).map(([k, n]) => (
              <span key={k} className="bg-gray-100 px-2 py-1 rounded">
                {k}: {n}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
        <h3 className="font-medium text-[#1B375B] mb-2">T1 launch checklist</h3>
        <ol className="list-decimal list-inside space-y-1 text-gray-700">
          {LAUNCH_STEPS.map((s) => (
            <li key={s.label}>
              <Link to={s.to} className="text-[#659A9B] underline">
                {s.label}
              </Link>
            </li>
          ))}
        </ol>
      </div>

      {data.pages_by_type && (
        <div className="bg-white border rounded-lg p-4">
          <h3 className="font-medium mb-2">Pages by type</h3>
          <div className="flex flex-wrap gap-3 text-sm">
            {Object.entries(data.pages_by_type).map(([t, n]) => (
              <Link key={t} to={`/admin/seo/pages?type=${t}`} className="text-[#659A9B] underline">
                {t}: {n}
              </Link>
            ))}
          </div>
        </div>
      )}
      {data.audit_summary && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-sm">
          <strong>Audit:</strong> {data.audit_flagged} flagged pages —{' '}
          <Link to="/admin/seo/audit" className="underline text-[#659A9B]">
            view audit
          </Link>
          <pre className="mt-2 text-xs">{JSON.stringify(data.audit_summary, null, 0)}</pre>
        </div>
      )}
      <div className="bg-gray-50 border rounded-lg p-4 text-sm text-gray-600">
        {data.gsc_placeholder?.message}
      </div>
    </div>
  );
}
